import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from '../api.service';
import {
  ItemImagemConsultaDTO,
  ItemImagensLoteDTO,
} from '../../models/produtos/item-imagem.model';

/**
 * Service de Imagens Secundarias do Item.
 *
 * Responsabilidades:
 *  - POST de lote (1 a 10 imagens) -> `adicionarImagens`
 *  - GET com cache em memoria      -> `buscarImagensSecundarias`
 *  - Invalidacao do cache          -> `invalidarCache`
 *
 * O cache vive enquanto o app rodar (`providedIn: 'root'`). Quem realiza POST
 * (componente de cadastro) deve chamar `invalidarCache(itemId)` apos sucesso
 * para garantir consistencia em recarregamentos futuros.
 */
@Injectable({ providedIn: 'root' })
export class ItemImagemService {
  /**
   * Cache em memoria por itemId.
   * Contem apenas listas de nomes de arquivos publicos - sem dado sensivel.
   */
  private readonly cache = new Map<number, ItemImagemConsultaDTO[]>();

  constructor(private apiService: ApiService) {}

  /**
   * Busca imagens secundarias de um item.
   * - Cache hit: retorna `of(cached)` sincronamente (zero round-trip).
   * - Cache miss: GET /item/{id}/imagens e popula o cache via `tap`.
   *
   * Lista ja vem ordenada por `ordem ASC, id ASC` (responsabilidade do backend).
   */
  buscarImagensSecundarias(itemId: number): Observable<ItemImagemConsultaDTO[]> {
    const cached = this.cache.get(itemId);
    if (cached !== undefined) {
      return of(cached);
    }
    return this.apiService
      .read<ItemImagemConsultaDTO[]>(`/item/${itemId}/imagens`)
      .pipe(tap((lista: ItemImagemConsultaDTO[]) => this.cache.set(itemId, lista)));
  }

  /**
   * Adiciona um lote (1 a 10 imagens) ao item.
   * Backend: POST /item/{id}/imagens (201 Created).
   *
   * NAO toca cache aqui - o chamador (componente de cadastro) e responsavel
   * por invalidar via `invalidarCache(itemId)` apos sucesso.
   */
  adicionarImagens(
    itemId: number,
    dto: ItemImagensLoteDTO,
  ): Observable<ItemImagemConsultaDTO[]> {
    return this.apiService.create<ItemImagemConsultaDTO[]>(
      `/item/${itemId}/imagens`,
      dto,
    );
  }

  /**
   * Remove uma imagem secundaria especifica do item (exclusao individual).
   * Backend: DELETE /item/{id}/imagens/{imagemId} (204 No Content).
   *
   * NAO toca o cache aqui - o chamador (componente de cadastro) invalida via
   * `invalidarCache(itemId)` apos sucesso.
   */
  removerImagem(itemId: number, imagemId: number): Observable<void> {
    return this.apiService.delete(`/item/${itemId}/imagens/${imagemId}`);
  }

  /**
   * Invalida o cache.
   * @param itemId Se fornecido, apaga somente a entry daquele item.
   *               Se omitido, limpa todo o cache.
   */
  invalidarCache(itemId?: number): void {
    if (itemId !== undefined) {
      this.cache.delete(itemId);
    } else {
      this.cache.clear();
    }
  }
}
